import { usePortalConfig } from "../context/PortalConfigContext";
import staticConfig from "../config/caseDetailsConfig.json";
import { applyTabOverrides, deepMerge } from "../config/clientConfig";
import { getClient } from "../config/clients";

// The client the user actually logged in as. Read straight from localStorage
// (rather than importing authService) to avoid circular-import issues — the
// same approach portalApiClient.js takes.
const getLoggedInSourceName = () => {
  try {
    return localStorage.getItem("source_name") || null;
  } catch {
    return null;
  }
};

/**
 * Returns the active page configuration, preferring the API-fetched portal
 * config and falling back to the bundled static JSON when unavailable.
 *
 * Shape returned is identical regardless of source so all consumers are
 * source-agnostic. To revert to static-only: delete this file and restore
 * the direct `import pageConfig from "../config/caseDetailsConfig.json"` and
 * `import clientConfig from "../config/clientConfig"` in each consumer.
 */
export function usePageConfig() {
  const { portalConfig, configLoading } = usePortalConfig();

  const apiConfig = portalConfig?.configuration;

  // Static fallback resolves to whoever is logged in, not the build-time
  // `client.active` — otherwise a Hegic session would fall back to Bajaj's
  // source name and media host whenever the portal config API is unreachable.
  const staticKey =
    getLoggedInSourceName() || staticConfig.client?.active || "bajaj";
  const staticClientConfig = getClient(staticKey) || {};

  /**
   * API config when available, static JSON otherwise — but the client's own tab
   * overrides apply on top of whichever won.
   *
   * They used to apply only to the static fallback, which made them inert in
   * every real session: the portal-config API almost always answers, and a
   * record seeded before an override existed cannot know about it. Enabling a
   * tab in src/config/clients/<key>.json then did nothing, and the only way to
   * turn one on was a manual PUT against the config API — per client, per
   * environment, out of version control.
   *
   * So an in-repo per-client fact now wins over the remote record for tab
   * visibility. That is the same call already made for the API host, and for
   * the same reason: the remote record goes stale silently.
   */
  /**
   * Layered, not replaced. The API record used to win outright, so any key
   * added to the shared file after a record was seeded simply vanished — the
   * page then read `.enabled` off undefined. Merging over the static defaults
   * means a stale record loses only the keys it actually states.
   */
  const base = deepMerge(staticConfig, apiConfig);
  const pageConfig = {
    ...base,
    tabs: applyTabOverrides(base.tabs, staticClientConfig.tabOverrides),
  };

  // API returns a flat client object (already resolved to the active client).
  // The static registry is one file per client, in src/config/clients/.
  // Normalise to the flat shape that all consumers expect.
  const clientConfig = apiConfig?.client || staticClientConfig;

  const apiSourceName =
    clientConfig.apiSourceName || clientConfig.sourceName || "bajaj";
  const mediaBaseUrl = clientConfig.mediaBaseUrl || "";

  return {
    pageConfig,
    clientConfig,
    apiSourceName,
    mediaBaseUrl,
    configLoading,
  };
}
